import { EyeIcon } from '@heroicons/react/24/outline';

import { IconButton } from '@/components/IconButton';
import { SeverityIndicator } from '@/components/SeverityIndicator';
import { getColumnWidth } from '@/components/table/columns/registry';
import { ColumnPriority, Columns } from '@/components/table/types';
import { Tooltip } from '@/components/Tooltip';
import {
  CustomerTypeTag,
  StatusBadge,
} from '@/sections/customerManagement/components/badges/StatusBadge';
import { CustomerNameWithLogo } from '@/sections/customerManagement/components/CustomerNameWithLogo';
import {
  DEFAULT_COLUMN_ORDER,
  isColumnSortable,
} from '@/sections/customerManagement/config/columns';
import { CustomerData, Filters } from '@/sections/customerManagement/types';
import { RiskSeverity } from '@/types';

export const getColumns = ({
  onViewAccount,
  isPraetorianUser = true,
}: {
  onViewAccount?: (customer: CustomerData) => void;
  isPraetorianUser?: boolean;
}): Columns<CustomerData> => {
  const columns: Columns<CustomerData> = [
    {
      label: 'View',
      id: '',
      cell: (customer: CustomerData) => (
        <IconButton
          icon={<EyeIcon className="size-5" />}
          onClick={e => {
            // Prevent row click handler from opening drawer when clicking View button
            e.stopPropagation();
            onViewAccount?.(customer);
          }}
          aria-label="View account"
          variant="primary"
          size="sm"
        />
      ),
      ...getColumnWidth('customer', 'view'),
      responsive: {
        priority: ColumnPriority.CRITICAL,
        alwaysVisible: true,
      },
    },
    {
      label: 'Status',
      id: 'status',
      isLoading: (customer: CustomerData) =>
        customer?.statusStatus === 'pending',
      cell: (customer: CustomerData) => (
        <StatusBadge status={customer.status} />
      ),
      sorting: isColumnSortable('status'),
      ...getColumnWidth('customer', 'status'),
      responsive: {
        priority: ColumnPriority.CRITICAL,
        alwaysVisible: true,
      },
    },
    {
      label: isPraetorianUser ? 'Customer' : 'Tenant',
      id: 'displayName',
      cell: (customer: CustomerData) => (
        <CustomerNameWithLogo customer={customer} />
      ),
      sorting: isColumnSortable('displayName'),
      ...getColumnWidth('customer', 'displayName'),
      responsive: {
        priority: ColumnPriority.CRITICAL,
        alwaysVisible: true,
      },
    },
  ];

  // Only show Type column for Praetorian users
  if (isPraetorianUser) {
    columns.push({
      label: 'Type',
      id: 'type',
      isLoading: (customer: CustomerData) => customer?.typeStatus === 'pending',
      cell: (customer: CustomerData) => (
        <CustomerTypeTag type={customer.type} />
      ),
      sorting: isColumnSortable('type'),
      ...getColumnWidth('customer', 'type'),
      responsive: {
        priority: ColumnPriority.LOW,
      },
    });
  }

  columns.push(
    {
      label: 'Health',
      id: 'health',
      isLoading: (customer: CustomerData) =>
        customer?.integrationHealthStatus === 'pending',
      responsive: {
        priority: ColumnPriority.LOW,
      },
      cell: (customer: CustomerData) => {
        const totalIntegrations = customer.totalIntegrations;
        const healthyIntegrations = customer.healthyIntegrations;
        const percent = (healthyIntegrations / totalIntegrations) * 100;
        const tooltip = `Customer has ${healthyIntegrations} healthy integrations out of ${totalIntegrations} total integrations`;

        let className = '';
        if (percent <= 33) {
          className = 'bg-chariot-stroke-critical';
        } else if (percent <= 66) {
          className = 'bg-chariot-stroke-medium';
        } else {
          className = 'bg-chariot-stroke-info';
        }

        if (totalIntegrations === 0) {
          return '-';
        }

        return (
          <span className="flex items-center gap-2">
            <Tooltip title={tooltip}>
              <span className={`size-4 rounded-full ${className}`} />
            </Tooltip>
            <span>{`${healthyIntegrations}/${totalIntegrations}`}</span>
          </span>
        );
      },
      sorting: isColumnSortable('health'),
      ...getColumnWidth('customer', 'health'),
    },
    {
      label: 'Critical',
      id: 'critical',
      isLoading: (customer: CustomerData) =>
        customer?.riskCountsStatus === 'pending',
      cell: (customer: CustomerData) => (
        <SeverityIndicator
          severity={RiskSeverity.Critical}
          count={customer.critical || 0}
          size="md"
          showZero
          animate
          data-testid={`severity-critical-${customer.email}`}
        />
      ),
      sorting: isColumnSortable('critical'),
      ...getColumnWidth('customer', 'critical'),
      responsive: {
        priority: ColumnPriority.MEDIUM,
      },
    },
    {
      label: 'High',
      id: 'high',
      isLoading: (customer: CustomerData) =>
        customer?.riskCountsStatus === 'pending',
      cell: (customer: CustomerData) => (
        <SeverityIndicator
          severity={RiskSeverity.High}
          count={customer.high || 0}
          size="md"
          showZero
          animate
          data-testid={`severity-high-${customer.email}`}
        />
      ),
      sorting: isColumnSortable('high'),
      ...getColumnWidth('customer', 'high'),
      responsive: {
        priority: ColumnPriority.MEDIUM,
      },
    },
    {
      label: 'Medium',
      id: 'medium',
      isLoading: (customer: CustomerData) =>
        customer?.riskCountsStatus === 'pending',
      cell: (customer: CustomerData) => (
        <SeverityIndicator
          severity={RiskSeverity.Medium}
          count={customer.medium || 0}
          size="md"
          showZero
          animate
          data-testid={`severity-medium-${customer.email}`}
        />
      ),
      sorting: isColumnSortable('medium'),
      ...getColumnWidth('customer', 'medium'),
      responsive: {
        priority: ColumnPriority.MEDIUM,
      },
    },
    {
      label: 'Low',
      id: 'low',
      isLoading: (customer: CustomerData) =>
        customer?.riskCountsStatus === 'pending',
      cell: (customer: CustomerData) => (
        <SeverityIndicator
          severity={RiskSeverity.Low}
          count={customer.low || 0}
          size="md"
          showZero
          animate
          data-testid={`severity-low-${customer.email}`}
        />
      ),
      sorting: isColumnSortable('low'),
      ...getColumnWidth('customer', 'low'),
      responsive: {
        priority: ColumnPriority.MEDIUM,
      },
    },
    {
      label: 'Info',
      id: 'info',
      isLoading: (customer: CustomerData) =>
        customer?.riskCountsStatus === 'pending',
      cell: (customer: CustomerData) => (
        <SeverityIndicator
          severity={RiskSeverity.Info}
          count={customer.info || 0}
          size="md"
          showZero
          animate
          data-testid={`severity-info-${customer.email}`}
        />
      ),
      sorting: isColumnSortable('info'),
      ...getColumnWidth('customer', 'info'),
    }
  );

  return columns;
};

export const defaultFilters: Filters = {
  search: '',
  advancedFilters: {},
};

export const getSortableColumnsConfig = ({
  defaultColumns,
}: {
  defaultColumns: Columns<CustomerData>;
}) => {
  // Use default column order from configuration
  const defaultOrder = [...DEFAULT_COLUMN_ORDER];

  // Find the view column (action column with empty string ID)
  const viewColumn = defaultColumns.find(col => col.label === 'View');

  // Build active columns with View at the beginning
  const activeColumns = defaultOrder
    .map(id => {
      const column = defaultColumns.find(col => col.id === id);
      return column
        ? {
            label: column.label,
            id: column.id,
            disabled: id === 'displayName',
            isDefault: id === 'displayName',
          }
        : null;
    })
    .filter(item => item !== null);

  // Prepend the view column to active columns
  if (viewColumn) {
    activeColumns.unshift({
      label: viewColumn.label,
      id: viewColumn.id,
      disabled: true,
      isDefault: true,
    });
  }

  return {
    fixed: [],
    active: activeColumns,
    available: [],
  };
};
